import * as React from 'react';
import { WithSearch } from '@elastic/react-search-ui';
import * as _ from 'lodash'
import { Config } from '../../helper';
import classnames from 'classnames';
import { getCategory } from '../../../faceted-search/react/search-results/CardSearchResult';
import { getFieldValue } from './helper';
// Generic badge overflow group (not OpenSearch-specific) — the seam we'll swap
// for Kaizen's OverflowGroup once the app is on React 18.
import BadgeOverflowGroup from '../../../../badge_overflow_group/BadgeOverflowGroup';

function Card(props) {
  return (
    <div className='card'>
      <div className='card__content'>{props.children}</div>
    </div>
  );
}

function PretitleField(props) {
  const {
    items = [],
    truncate = false,
    separator
  } = props;

  const rootEl = React.useRef(null);
  const contentEl = React.useRef(null);

  const css = {
    root: classnames({
      'pretitle': true,
      'pretitle--truncated': true,
      'mb-2': true,
    }),
    content: classnames({
      'p--medium': true,
      'text-bold': true,
      'pretitle__text': true,
      'text-truncate': !!truncate,
    }),
  };

  React.useEffect(() => {
    if (truncate && rootEl.current) {
      const initialRect = rootEl.current.getBoundingClientRect();
      contentEl.current.classList.remove('text-truncate');
      const originalRect = rootEl.current.getBoundingClientRect();
      if (originalRect.height > initialRect.height) {
        contentEl.current.classList.add('pretitle--clickable');
        contentEl.current.classList.add('text-truncate');
      }
    }
  }, []);

  const clickHandler = (evt) => {
    if(!contentEl?.current) {
      return;
    }
    contentEl?.current.classList.toggle('text-truncate');
  };

  return (
    <div className={css.root} ref={rootEl} onClick={clickHandler}>
      <div className={css.content} ref={contentEl}>{items.join(separator)}</div>
    </div>
  );
}

function Title({text}) {
  return (
    <h3 className='h--smallest title'>{text}</h3>
  );
}

function Badges({items = []}) {
  const badges = items.map(item => ({
    label: item,
    category: getCategory(item.toLowerCase())
  }));

  return <BadgeOverflowGroup badges={badges}/>;
}

function Body({text}) {
  return (
    <div className='mt-0 mb-0 text' title={text}>{text}</div>
  );
}

function StretchedLink({url}) {
  return (
    <a className='stretched-link' rel='nofollow' href={url}></a>
  );
}

export default function CardSearchResult(props) {
  const {displayOptions} = props;
  const {
    mapping,
    truncatePretitle = false,
    clickableCard = false,
  } = displayOptions;

  return (
    <WithSearch mapContextToProps={({results, wasSearched}) => ({results, wasSearched})}>
      {({results, wasSearched}) => {
        if (!wasSearched) return null;

        if (results.length === 0) return <p className='h--smaller mb-4'>{Config.I18n.Hits.NoResults}</p>;

        return (
          <div className='search-results'>
            {results.map(r => {
              const fieldValue = (fieldName, defaultValue) => {
                return getFieldValue(r, mapping, fieldName, defaultValue) || defaultValue;
              };

              const id = _.get(r, 'id.raw', '');
              const index = _.get(r, '_meta.rawHit._index', '');
              const titleText = fieldValue(Config.Mapping.Card.Title, '')
              const featured = fieldValue(Config.Mapping.Card.Featured, false);
              let badges = fieldValue(Config.Mapping.Card.Badges, []);
              const pretitleItems = fieldValue(Config.Mapping.Card.Pretitle, []);
              const text = fieldValue(Config.Mapping.Card.Body, null);
              const rawLinks = fieldValue(Config.Mapping.Card.Links, '');
              const links = [];

              if (rawLinks && typeof rawLinks === 'string' && rawLinks.length) {
                links.push({text: Config.I18n.Hits.LearnMore, url: rawLinks})
              }

              if (featured) {
                badges = [Config.I18n.Hits.Featured, ...badges];
              }

              return (
                <Card key={`${index}:${id}`}>
                  {badges.length > 0 && <Badges items={badges}/>}
                  <PretitleField truncate={truncatePretitle} items={pretitleItems} separator={' | '}/>
                  <Title text={titleText}/>
                  {text && <Body text={text}/>}
                  {clickableCard && links?.[0] && <StretchedLink url={links[0].url}/>}
                </Card>
              );
            })}
          </div>
        );
      }}
    </WithSearch>
  );
}
